// Niper i18n — Italian / English dictionaries + provider hook.
// Used everywhere via `useT()` returning a `t(key, vars?)` function.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const SUPPORTED_LANGS = ["it", "en"];
const STORAGE_KEY = "niper_lang";

const DICT = {
  it: {
    // Brand
    "brand.name": "Niper",
    "brand.tagline": "Nihil Perditum",
    "brand.translation": "nulla è perduto.",

    // Common
    "common.cancel": "Annulla",
    "common.save": "Salva",
    "common.delete": "Elimina",
    "common.confirm": "Conferma",
    "common.back": "Indietro",
    "common.add": "Aggiungi",
    "common.edit": "Modifica",
    "common.search": "Cerca",
    "common.loading": "Caricamento…",
    "common.error": "Errore",
    "common.required": "Campo obbligatorio",
    "common.no_results": "Nessun risultato.",
    "common.products": "prodotti",
    "common.product": "prodotto",
    "common.qty": "Qty",
    "common.pcs": "pz",
    "common.days_left": "{{n}} giorni rimasti",
    "common.expired_days": "scaduto da {{n}}g",
    "common.copy": "Copia",
    "common.share": "Condividi",
    "common.signin_google": "Continua con Google",
    "common.signin_join": "Entra e unisciti a {{code}}",

    // Language
    "lang.title": "Scegli la lingua",
    "lang.subtitle": "Choose your language",
    "lang.italian": "Italiano",
    "lang.english": "English (US)",
    "lang.confirm": "Continua",
    "lang.label": "Lingua",

    // Landing
    "landing.fresh_tracker": "Fresh tracker",
    "landing.hero.line1": "Nihil",
    "landing.hero.line2_red": "Perditum.",
    "landing.hero.line2_yellow": "Nulla",
    "landing.hero.line2_green": "è perduto.",
    "landing.subtitle": "Scansiona la data sulla confezione: Niper fa il resto. Condividi la tua dispensa con più membri della tua famiglia o del tuo team.",
    "landing.invite.divider": "Hai un invito?",
    "landing.invite.placeholder": "A3B7XZ",
    "landing.invite.hint": "Dopo l'accesso ti uniremo automaticamente alla dispensa {{code}}",
    "landing.privacy": "Sicuro · Solo HTTPS · La tua dispensa resta privata nella tua famiglia",

    // Dashboard
    "dashboard.title.in_pantry": "in dispensa.",
    "dashboard.stats.critical": "Critici",
    "dashboard.stats.soon": "Presto",
    "dashboard.stats.fresh": "Freschi",
    "dashboard.stats.expired": "Scaduti",
    "dashboard.fresh_filter.active": "Filtro:",
    "dashboard.fresh_filter.clear": "Rimuovi",
    "dashboard.search.placeholder": "Cerca prodotto…",
    "dashboard.cat.all": "Tutte",
    "dashboard.cat.none": "Senza categoria",
    "dashboard.cat.manage": "Gestisci",
    "dashboard.banner.pending.label": "Da inserire",
    "dashboard.banner.pending.desc": "{{n}} prodotti pronti · controlla e conferma",
    "dashboard.banner.confirm_all": "Inserisci tutto",
    "dashboard.banner.scanning.label": "Elaborazione IA",
    "dashboard.banner.scanning.desc": "{{n}} foto in analisi…",
    "dashboard.banner.review.label": "Da rivedere",
    "dashboard.banner.review.desc": "{{n}} scatti senza dati completi · tocca per correggere",
    "dashboard.group.red": "Da mangiare subito",
    "dashboard.group.yellow": "Da usare presto",
    "dashboard.group.green": "Tempo a sufficienza",
    "dashboard.group.expired": "Scaduti",
    "dashboard.group.pending": "Da inserire",
    "dashboard.group.scanning": "In analisi",
    "dashboard.group.review": "Da rivedere",
    "dashboard.empty.title": "Dispensa vuota.",
    "dashboard.empty.desc": "Tocca il pulsante centrale per iniziare.",
    "dashboard.empty.manual": "Aggiungi manualmente",
    "dashboard.filter_empty.title": "Nessun prodotto per i filtri selezionati.",
    "dashboard.filter_empty.add": "+ Aggiungi manualmente",
    "dashboard.search_empty": "Nessun prodotto corrisponde a “{{q}}”.",
    "dashboard.toast.consumed": "{{name}} finito",
    "dashboard.toast.remaining": "Rimangono {{n}} pz",
    "dashboard.toast.deleted": "Eliminato",
    "dashboard.toast.error": "Errore",
    "dashboard.toast.inserted": "{{name}} inserito in “{{group}}”",
    "dashboard.toast.confirmed_n": "{{n}} prodotti inseriti in dispensa",
    "dashboard.toast.stale_warn": "Data scaduta ({{date}}). Correggila prima di inserire.",
    "dashboard.toast.stale_n": "{{n}} prodotti hanno date scadute. Controllali uno a uno prima di inserire.",
    "dashboard.toast.created_in": "Inserito in “{{group}}”",
    "dashboard.toast.create_failed": "Creazione fallita",
    "dashboard.toast.saved": "Modifiche salvate",
    "dashboard.toast.save_failed": "Salvataggio fallito",
    "dashboard.toast.required": "Nome e scadenza obbligatori",
    "dashboard.toast.cant_load": "Impossibile caricare la dispensa",
    "drawer.title.new": "Nuovo prodotto",
    "drawer.title.pending": "Completa i dati",
    "drawer.title.edit": "Modifica prodotto",
    "drawer.desc.new": "Inserisci il prodotto manualmente.",
    "drawer.desc.pending": "Controlla e salva per inserirlo in dispensa.",
    "drawer.desc.edit": "Aggiorna i dettagli del prodotto.",
    "drawer.field.product": "Prodotto",
    "drawer.field.product.placeholder": "es. Latte fresco",
    "drawer.field.expiry": "Scadenza",
    "drawer.field.qty": "Quantità",
    "drawer.field.cat": "Categoria",
    "drawer.cat.none": "Nessuna",
    "drawer.cat.manage": "Gestisci",
    "drawer.btn.add": "Aggiungi",
    "drawer.btn.discard": "Scarta",
    "drawer.btn.save_confirm": "Salva e inserisci",
    "drawer.btn.save": "Salva",
    "drawer.btn.cancel": "Annulla",
    "row.btn.finish": "Finito",
    "row.label.expires_on": "Scade {{date}}",
    "row.modify": "Modifica",
    "row.fix_date": "Correggi data",
    "row.insert": "Inserisci",
    "row.review.title": "Prodotto da completare",
    "row.review.fix": "Correggi",
    "row.review.discard": "Scarta",
    "row.scanning.title": "Scansione in corso…",
    "row.scanning.desc": "Gemini sta leggendo l'etichetta",
    "row.stale_warn": "⚠ Data scaduta ({{date}}) · tocca per correggere",

    // BottomNav
    "nav.home": "Home",
    "nav.suggestions": "Idee",
    "nav.settings": "Impostazioni",
    "fab.scan": "Scansiona",
    "fab.scan.desc": "Fotocamera + OCR",
    "fab.manual": "Manuale",
    "fab.manual.desc": "Inserisci senza foto",

    // Settings
    "settings.eyebrow": "Impostazioni",
    "settings.title": "La tua dispensa",
    "settings.pantry": "Dispensa",
    "settings.invite_code": "Codice invito",
    "settings.share": "Condividi invito",
    "settings.toast.code_copied": "Codice copiato",
    "settings.toast.link_copied": "Link copiato",
    "settings.cat.title": "Categorie",
    "settings.cat.desc": "Crea, organizza e ordina le tue categorie con sottocategorie.",
    "settings.admin.title": "Console super admin",
    "settings.admin.desc": "Gestisci utenti, approvazioni e dispense.",
    "settings.members": "Membri",
    "settings.members.owner": "Owner",
    "settings.signout": "Disconnetti",
    "settings.signed_as": "Connesso come {{email}}",
    "settings.lang.title": "Lingua e mercato",
    "settings.lang.desc": "Cambia lingua e formato data.",

    // Onboarding
    "onb.title": "Crea o unisciti a una dispensa",
    "onb.subtitle": "Le dispense sono condivise con la tua famiglia.",
    "onb.tab.create": "Crea",
    "onb.tab.join": "Unisciti",
    "onb.create.label": "Nome dispensa",
    "onb.create.placeholder": "Casa Rossi",
    "onb.create.btn": "Crea dispensa",
    "onb.join.label": "Codice invito",
    "onb.join.placeholder": "A3B7XZ",
    "onb.join.btn": "Unisciti",
    "onb.toast.created": "Dispensa creata",
    "onb.toast.joined": "Sei nella dispensa",
    "onb.toast.invalid_code": "Codice invito non valido",

    // Pending approval
    "pending.title.disabled": "Account disattivato",
    "pending.title.expired": "Prova terminata",
    "pending.title.pending": "In attesa di approvazione",
    "pending.body.disabled": "Il tuo account è stato disattivato dall'amministratore.",
    "pending.body.expired": "La tua prova gratuita di 7 giorni è conclusa. L'amministratore può attivare definitivamente l'account.",
    "pending.body.contact": "Contatta {{email}} per sbloccare la dispensa.",
    "pending.account.title": "Il tuo account",
    "pending.contact_btn": "Scrivi all'amministratore",
    "pending.logout_btn": "Esci",

    // Notifications
    "notif.title": "Notifiche scadenze",
    "notif.desc.on": "Riceverai un avviso ogni volta che ci sono prodotti rossi o scaduti in dispensa.",
    "notif.desc.off": "Attivale per ricevere un avviso quando ci sono prodotti rossi o scaduti in dispensa.",
    "notif.enable_btn": "Attiva notifiche",
    "notif.disable": "Disattiva",
    "notif.test_btn": "Invia notifica di prova",
    "notif.test_sent": "Notifica inviata ({{n}})",
    "notif.test_no_alerts": "Nessun prodotto in scadenza al momento.",
    "notif.test_no_subs": "Nessuna sottoscrizione attiva. Attiva prima le notifiche.",
    "notif.enabled": "Notifiche attive",
    "notif.disabled": "Notifiche disattivate",
    "notif.denied": "Permesso negato dal browser. Sblocca le notifiche per Niper nelle impostazioni del browser.",
    "notif.permission_denied": "Le notifiche sono bloccate. Sbloccale dalle impostazioni del browser per Niper.",
    "notif.unsupported": "Le notifiche push non sono supportate da questo browser.",
    "notif.server_off": "Notifiche disabilitate sul server.",

    // Pantry switcher
    "switcher.title": "Le tue dispense",
    "switcher.subtitle": "Cambia dispensa, creane una nuova o unisciti con un codice invito.",
    "switcher.tab.mine": "Le mie",
    "switcher.tab.create": "Nuova",
    "switcher.tab.join": "Unisciti",
    "switcher.empty": "Nessuna dispensa ancora.",

    // Install PWA
    "install.title": "Installa Niper",
    "install.desc.android": "Aggiungi l'app alla schermata Home per accedere offline e con un tap.",
    "install.desc.ios": "Su iPhone: tocca Condividi e poi “Aggiungi a Home”.",
    "install.desc.fallback": "Apri Niper dal browser del telefono per installarla sulla Home.",
    "install.installed": "App già installata sulla Home.",
    "install.btn": "Installa app",
    "install.help.ios.intro": "Su iPhone l'installazione si fa dal menu Condividi del browser. Tre passi:",
    "install.help.ios.step1": "Tocca l'icona Condividi nella barra del browser.",
    "install.help.ios.step2": "Scorri e tocca “Aggiungi a Home”.",
    "install.help.ios.step3": "Conferma “Aggiungi”: l'icona Niper apparirà sulla schermata Home.",
    "install.help.desktop.intro": "Apri Niper dal browser del telefono per installarla. In alternativa, da desktop puoi usare Chrome o Edge:",
    "install.help.desktop.step1": "Apri il sito Niper nel browser.",
    "install.help.desktop.step2": "Cerca l'icona “Installa app” nella barra degli indirizzi.",
    "install.help.desktop.step3": "Conferma per aggiungerla al desktop o alla Home.",
    "install.dismiss": "Più tardi",
    "install.ios.share": "Tocca",
    "install.ios.then": "poi “Aggiungi a Home”",

    // AddItem (camera)
    "add.eyebrow": "Burst",
    "add.title": "Inquadra l'etichetta",
    "add.desc": "Tocca per scattare; più foto in serie",
    "add.shoot": "Scatta",
    "add.done": "Ho finito",
    "add.cancel": "Annulla",
    "add.permission": "Permessi fotocamera negati",

    // Suggestions
    "sugg.eyebrow": "Idee",
    "sugg.title": "Da ricomprare",
    "sugg.subtitle": "Prodotti finiti spesso consumati: aggiungili in un tocco.",
    "sugg.empty": "Nessun suggerimento ancora. Continua a usare la dispensa!",
    "sugg.add_again": "Aggiungi di nuovo",

    // Admin
    "admin.eyebrow": "Super Admin",
    "admin.title": "Console utenti",
    "admin.search.placeholder": "Cerca email, nome, dispensa…",
    "admin.filter.all": "Tutti",
    "admin.status.super_admin": "Super Admin",
    "admin.status.approved": "Attivo",
    "admin.status.trial": "In prova",
    "admin.status.expired": "Trial scaduto",
    "admin.status.disabled": "Disattivo",
    "admin.no_users": "Nessun utente.",
    "admin.row.products_active": "Prodotti attivi",
    "admin.section.pantry": "Dispensa",
    "admin.section.empty": "Nessun prodotto attivo.",
    "admin.user.created": "Creato il",
    "admin.user.trial_until": "Prova fino al",
    "admin.user.permanent": "Permanente",
    "admin.user.status": "Stato",
    "admin.user.household": "Dispensa",
    "admin.action.approve": "Approva (permanente)",
    "admin.action.extend7": "+7 giorni",
    "admin.action.extend30": "+30 giorni",
    "admin.action.disable": "Disattiva account",
    "admin.confirm.disable": "Disattivare {{email}}? Le sessioni attive verranno revocate.",
    "admin.confirm.disable_title": "Disattiva utente",
    "admin.toast.approved": "{{email}} approvato",
    "admin.toast.disabled": "{{email}} disattivato",
    "admin.toast.extended": "Prova estesa di {{n}} giorni",
    "admin.toast.unauthorized": "Non autorizzato",
    "admin.super_admin_locked": "Account super admin — non modificabile",

    // CategoryManager
    "cat.title": "Gestisci categorie",
    "cat.subtitle": "Crea, rinomina ed elimina categorie e sottocategorie.",
    "cat.new_root": "Nuova categoria",
    "cat.new_child": "Sottocategoria",
    "cat.placeholder.parent": "Nome categoria",
    "cat.placeholder.child": "Nome sottocategoria",
    "cat.empty": "Nessuna categoria. Aggiungine una!",
    "cat.confirm.delete": "Elimini “{{name}}”?",
    "cat.confirm.delete_with_children": "Elimini “{{name}}” e le sue {{n}} sottocategorie?",
    "cat.toast.added": "Categoria aggiunta",
    "cat.toast.updated": "Modificata",
    "cat.toast.deleted": "Eliminata",
    "cat.toast.moved_top": "Spostata in alto",
    "cat.subcount": "{{n}} sottocat.",

    // Plan picker
    "plan.eyebrow": "Scegli il pacchetto",
    "plan.title": "Privato o azienda?",
    "plan.subtitle": "Decidi come usare Niper. La scelta determina i limiti del tuo account (modificabili solo dal super admin).",
    "plan.private.title": "Privato",
    "plan.private.desc": "Per la tua famiglia o casa",
    "plan.business.title": "Azienda",
    "plan.business.desc": "Per ristoranti, B&B, mense, magazzini",
    "plan.bullet.households": "Fino a {{n}} dispense",
    "plan.bullet.members": "Fino a {{n}} membri per dispensa",
    "plan.bullet.items": "Fino a {{n}} prodotti attivi",
    "plan.continue": "Continua",
    "plan.note": "La scelta è permanente. Solo il super admin può modificarla in seguito.",
    "plan.required": "Scegli il tipo di account prima di proseguire.",
    "plan.limit.households": "Hai raggiunto il limite di dispense del tuo pacchetto ({{n}}).",
    "plan.limit.members": "Questa dispensa ha raggiunto il numero massimo di membri ({{n}}).",
    "plan.limit.items": "Hai raggiunto il numero massimo di prodotti del tuo pacchetto ({{n}}).",

    // Settings — plan/limits
    "settings.plan.title": "Pacchetto",
    "settings.plan.private": "Privato",
    "settings.plan.business": "Azienda",
    "settings.plan.unlimited": "Super Admin · Illimitato",
    "settings.plan.unset": "Non impostato",
    "settings.usage.title": "Utilizzo",
    "settings.usage.households": "Dispense possedute",
    "settings.usage.members": "Membri in dispensa",
    "settings.usage.items": "Prodotti attivi",

    // Admin — plans
    "admin.tab.users": "Utenti",
    "admin.tab.plans": "Pacchetti",
    "admin.plans.title": "Limiti pacchetti",
    "admin.plans.subtitle": "Configura i limiti per il pacchetto Privato e Azienda. Si applicano subito a tutti gli utenti.",
    "admin.plans.field.households": "Dispense max",
    "admin.plans.field.members": "Membri max per dispensa",
    "admin.plans.field.items": "Prodotti max per account",
    "admin.plans.save": "Salva limiti",
    "admin.plans.saved": "Limiti aggiornati",
    "admin.plans.reset": "Ripristina default",

    // Months (short, used by DateField)
    "months.short": ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
  },

  en: {
    "brand.name": "Niper",
    "brand.tagline": "Nihil Perditum",
    "brand.translation": "nothing is lost.",

    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.confirm": "Confirm",
    "common.back": "Back",
    "common.add": "Add",
    "common.edit": "Edit",
    "common.search": "Search",
    "common.loading": "Loading…",
    "common.error": "Error",
    "common.required": "Required field",
    "common.no_results": "No results.",
    "common.products": "items",
    "common.product": "item",
    "common.qty": "Qty",
    "common.pcs": "pcs",
    "common.days_left": "{{n}} days left",
    "common.expired_days": "expired {{n}}d ago",
    "common.copy": "Copy",
    "common.share": "Share",
    "common.signin_google": "Continue with Google",
    "common.signin_join": "Sign in & join {{code}}",

    "lang.title": "Choose your language",
    "lang.subtitle": "Scegli la lingua",
    "lang.italian": "Italian",
    "lang.english": "English (US)",
    "lang.confirm": "Continue",
    "lang.label": "Language",

    "landing.fresh_tracker": "Fresh tracker",
    "landing.hero.line1": "Stop wasting",
    "landing.hero.line2_red": "food.",
    "landing.hero.line2_yellow": "Save",
    "landing.hero.line2_green": "money.",
    "landing.subtitle": "Scan the date on the package: Niper does the rest. Share your pantry with multiple family or team members.",
    "landing.invite.divider": "Have an invite?",
    "landing.invite.placeholder": "A3B7XZ",
    "landing.invite.hint": "We'll auto-join household {{code}} after sign-in",
    "landing.privacy": "Secure · HTTPS-only · Your pantry stays private to your household",

    "dashboard.title.in_pantry": "in your pantry.",
    "dashboard.stats.critical": "Urgent",
    "dashboard.stats.soon": "Soon",
    "dashboard.stats.fresh": "Fresh",
    "dashboard.stats.expired": "Expired",
    "dashboard.fresh_filter.active": "Filter:",
    "dashboard.fresh_filter.clear": "Clear",
    "dashboard.search.placeholder": "Search items…",
    "dashboard.cat.all": "All",
    "dashboard.cat.none": "Uncategorized",
    "dashboard.cat.manage": "Manage",
    "dashboard.banner.pending.label": "To add",
    "dashboard.banner.pending.desc": "{{n}} items ready · review & confirm",
    "dashboard.banner.confirm_all": "Add all",
    "dashboard.banner.scanning.label": "AI processing",
    "dashboard.banner.scanning.desc": "Analyzing {{n}} photos…",
    "dashboard.banner.review.label": "Needs review",
    "dashboard.banner.review.desc": "{{n}} captures incomplete · tap to fix",
    "dashboard.group.red": "Eat soon",
    "dashboard.group.yellow": "Use this week",
    "dashboard.group.green": "Plenty of time",
    "dashboard.group.expired": "Expired",
    "dashboard.group.pending": "To add",
    "dashboard.group.scanning": "Scanning",
    "dashboard.group.review": "Review",
    "dashboard.empty.title": "Empty pantry.",
    "dashboard.empty.desc": "Tap the center button to start.",
    "dashboard.empty.manual": "Add manually",
    "dashboard.filter_empty.title": "No items match these filters.",
    "dashboard.filter_empty.add": "+ Add manually",
    "dashboard.search_empty": "No items match “{{q}}”.",
    "dashboard.toast.consumed": "{{name}} consumed",
    "dashboard.toast.remaining": "{{n}} pcs left",
    "dashboard.toast.deleted": "Deleted",
    "dashboard.toast.error": "Error",
    "dashboard.toast.inserted": "{{name}} added to “{{group}}”",
    "dashboard.toast.confirmed_n": "{{n}} items added to pantry",
    "dashboard.toast.stale_warn": "Date is in the past ({{date}}). Fix it before adding.",
    "dashboard.toast.stale_n": "{{n}} items have past dates. Review them one by one.",
    "dashboard.toast.created_in": "Added to “{{group}}”",
    "dashboard.toast.create_failed": "Could not create",
    "dashboard.toast.saved": "Changes saved",
    "dashboard.toast.save_failed": "Could not save",
    "dashboard.toast.required": "Name and date required",
    "dashboard.toast.cant_load": "Could not load pantry",
    "drawer.title.new": "New item",
    "drawer.title.pending": "Complete the data",
    "drawer.title.edit": "Edit item",
    "drawer.desc.new": "Add the item manually.",
    "drawer.desc.pending": "Review and save to add to pantry.",
    "drawer.desc.edit": "Update item details.",
    "drawer.field.product": "Item",
    "drawer.field.product.placeholder": "e.g. Whole milk",
    "drawer.field.expiry": "Expires on",
    "drawer.field.qty": "Quantity",
    "drawer.field.cat": "Category",
    "drawer.cat.none": "None",
    "drawer.cat.manage": "Manage",
    "drawer.btn.add": "Add",
    "drawer.btn.discard": "Discard",
    "drawer.btn.save_confirm": "Save & add",
    "drawer.btn.save": "Save",
    "drawer.btn.cancel": "Cancel",
    "row.btn.finish": "Done",
    "row.label.expires_on": "Expires {{date}}",
    "row.modify": "Edit",
    "row.fix_date": "Fix date",
    "row.insert": "Add",
    "row.review.title": "Item to complete",
    "row.review.fix": "Fix",
    "row.review.discard": "Discard",
    "row.scanning.title": "Scanning…",
    "row.scanning.desc": "Gemini is reading the label",
    "row.stale_warn": "⚠ Past date ({{date}}) · tap to fix",

    "nav.home": "Home",
    "nav.suggestions": "Ideas",
    "nav.settings": "Settings",
    "fab.scan": "Scan",
    "fab.scan.desc": "Camera + OCR",
    "fab.manual": "Manual",
    "fab.manual.desc": "Add without photo",

    "settings.eyebrow": "Settings",
    "settings.title": "Your pantry",
    "settings.pantry": "Pantry",
    "settings.invite_code": "Invite code",
    "settings.share": "Share invite",
    "settings.toast.code_copied": "Code copied",
    "settings.toast.link_copied": "Link copied",
    "settings.cat.title": "Categories",
    "settings.cat.desc": "Create, organize and order your categories and subcategories.",
    "settings.admin.title": "Super admin console",
    "settings.admin.desc": "Manage users, approvals and pantries.",
    "settings.members": "Members",
    "settings.members.owner": "Owner",
    "settings.signout": "Sign out",
    "settings.signed_as": "Signed in as {{email}}",
    "settings.lang.title": "Language & market",
    "settings.lang.desc": "Change language and date format.",

    "onb.title": "Create or join a pantry",
    "onb.subtitle": "Pantries are shared with your family.",
    "onb.tab.create": "Create",
    "onb.tab.join": "Join",
    "onb.create.label": "Pantry name",
    "onb.create.placeholder": "The Smith Family",
    "onb.create.btn": "Create pantry",
    "onb.join.label": "Invite code",
    "onb.join.placeholder": "A3B7XZ",
    "onb.join.btn": "Join",
    "onb.toast.created": "Pantry created",
    "onb.toast.joined": "Joined pantry",
    "onb.toast.invalid_code": "Invalid invite code",

    "pending.title.disabled": "Account disabled",
    "pending.title.expired": "Trial ended",
    "pending.title.pending": "Awaiting approval",
    "pending.body.disabled": "Your account has been disabled by the administrator.",
    "pending.body.expired": "Your free 7-day trial has ended. The administrator can permanently activate your account.",
    "pending.body.contact": "Email {{email}} to unlock the pantry.",
    "pending.account.title": "Your account",
    "pending.contact_btn": "Email administrator",
    "pending.logout_btn": "Sign out",

    "notif.title": "Expiry notifications",
    "notif.desc.on": "You'll get an alert whenever there are red or expired products in your pantry.",
    "notif.desc.off": "Enable to get an alert when there are red or expired products in your pantry.",
    "notif.enable_btn": "Enable notifications",
    "notif.disable": "Disable",
    "notif.test_btn": "Send test notification",
    "notif.test_sent": "Notification sent ({{n}})",
    "notif.test_no_alerts": "No expiring items right now.",
    "notif.test_no_subs": "No active subscription. Enable notifications first.",
    "notif.enabled": "Notifications enabled",
    "notif.disabled": "Notifications disabled",
    "notif.denied": "Permission denied by the browser. Unblock Niper notifications in your browser settings.",
    "notif.permission_denied": "Notifications are blocked. Unblock them in your browser settings for Niper.",
    "notif.unsupported": "Push notifications are not supported by this browser.",
    "notif.server_off": "Notifications disabled on server.",

    "switcher.title": "Your pantries",
    "switcher.subtitle": "Switch pantry, create a new one, or join with an invite code.",
    "switcher.tab.mine": "Mine",
    "switcher.tab.create": "Create",
    "switcher.tab.join": "Join",
    "switcher.empty": "No pantries yet.",

    "install.title": "Install Niper",
    "install.desc.android": "Add the app to your Home screen for offline access and one-tap launch.",
    "install.desc.ios": "On iPhone: tap Share, then “Add to Home Screen”.",
    "install.desc.fallback": "Open Niper from your phone browser to install it on the Home screen.",
    "install.installed": "App already installed on Home screen.",
    "install.btn": "Install app",
    "install.help.ios.intro": "On iPhone you install Niper from the browser Share menu. Three steps:",
    "install.help.ios.step1": "Tap the Share icon in the browser bar.",
    "install.help.ios.step2": "Scroll and tap “Add to Home Screen”.",
    "install.help.ios.step3": "Confirm “Add”: the Niper icon will appear on your Home Screen.",
    "install.help.desktop.intro": "Open Niper from your phone browser to install it. On desktop you can use Chrome or Edge:",
    "install.help.desktop.step1": "Open Niper in your browser.",
    "install.help.desktop.step2": "Look for the “Install app” icon in the address bar.",
    "install.help.desktop.step3": "Confirm to add it to your desktop or Home screen.",
    "install.dismiss": "Maybe later",
    "install.ios.share": "Tap",
    "install.ios.then": "then “Add to Home Screen”",

    "add.eyebrow": "Burst",
    "add.title": "Frame the label",
    "add.desc": "Tap to capture; chain several photos",
    "add.shoot": "Capture",
    "add.done": "Done",
    "add.cancel": "Cancel",
    "add.permission": "Camera permission denied",

    "sugg.eyebrow": "Ideas",
    "sugg.title": "Buy again",
    "sugg.subtitle": "Items you finish often: re-add them with one tap.",
    "sugg.empty": "No suggestions yet. Keep using your pantry!",
    "sugg.add_again": "Add again",

    "admin.eyebrow": "Super Admin",
    "admin.title": "Users console",
    "admin.search.placeholder": "Search email, name, pantry…",
    "admin.filter.all": "All",
    "admin.status.super_admin": "Super Admin",
    "admin.status.approved": "Active",
    "admin.status.trial": "Trial",
    "admin.status.expired": "Trial ended",
    "admin.status.disabled": "Disabled",
    "admin.no_users": "No users.",
    "admin.row.products_active": "Active items",
    "admin.section.pantry": "Pantry",
    "admin.section.empty": "No active items.",
    "admin.user.created": "Created on",
    "admin.user.trial_until": "Trial until",
    "admin.user.permanent": "Permanent",
    "admin.user.status": "Status",
    "admin.user.household": "Pantry",
    "admin.action.approve": "Approve (permanent)",
    "admin.action.extend7": "+7 days",
    "admin.action.extend30": "+30 days",
    "admin.action.disable": "Disable account",
    "admin.confirm.disable": "Disable {{email}}? Active sessions will be revoked.",
    "admin.confirm.disable_title": "Disable user",
    "admin.toast.approved": "{{email}} approved",
    "admin.toast.disabled": "{{email}} disabled",
    "admin.toast.extended": "Trial extended by {{n}} days",
    "admin.toast.unauthorized": "Unauthorized",
    "admin.super_admin_locked": "Super admin account — locked",

    "cat.title": "Manage categories",
    "cat.subtitle": "Create, rename and delete categories and subcategories.",
    "cat.new_root": "New category",
    "cat.new_child": "Subcategory",
    "cat.placeholder.parent": "Category name",
    "cat.placeholder.child": "Subcategory name",
    "cat.empty": "No categories yet. Add one!",
    "cat.confirm.delete": "Delete “{{name}}”?",
    "cat.confirm.delete_with_children": "Delete “{{name}}” and its {{n}} subcategories?",
    "cat.toast.added": "Category added",
    "cat.toast.updated": "Updated",
    "cat.toast.deleted": "Deleted",
    "cat.toast.moved_top": "Moved to top level",
    "cat.subcount": "{{n}} subcat.",

    // Plan picker
    "plan.eyebrow": "Choose your plan",
    "plan.title": "Private or business?",
    "plan.subtitle": "Decide how you'll use Niper. Your choice sets the limits of your account (only the super admin can change it later).",
    "plan.private.title": "Private",
    "plan.private.desc": "For your family or home",
    "plan.business.title": "Business",
    "plan.business.desc": "For restaurants, B&Bs, canteens, warehouses",
    "plan.bullet.households": "Up to {{n}} pantries",
    "plan.bullet.members": "Up to {{n}} members per pantry",
    "plan.bullet.items": "Up to {{n}} active items",
    "plan.continue": "Continue",
    "plan.note": "This choice is permanent. Only the super admin can change it later.",
    "plan.required": "Pick an account type before continuing.",
    "plan.limit.households": "You've reached your plan limit of pantries ({{n}}).",
    "plan.limit.members": "This pantry already has the maximum number of members ({{n}}).",
    "plan.limit.items": "You've reached your plan limit of items ({{n}}).",

    "settings.plan.title": "Plan",
    "settings.plan.private": "Private",
    "settings.plan.business": "Business",
    "settings.plan.unlimited": "Super Admin · Unlimited",
    "settings.plan.unset": "Not set",
    "settings.usage.title": "Usage",
    "settings.usage.households": "Pantries owned",
    "settings.usage.members": "Members in pantry",
    "settings.usage.items": "Active items",

    "admin.tab.users": "Users",
    "admin.tab.plans": "Plans",
    "admin.plans.title": "Plan limits",
    "admin.plans.subtitle": "Configure limits for Private and Business plans. Applies immediately to all users.",
    "admin.plans.field.households": "Max pantries",
    "admin.plans.field.members": "Max members per pantry",
    "admin.plans.field.items": "Max items per account",
    "admin.plans.save": "Save limits",
    "admin.plans.saved": "Limits updated",
    "admin.plans.reset": "Reset to defaults",

    "months.short": ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  },
};

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
  } catch (_e) { /* noop */ }
  // No stored choice → leave null so the app can prompt.
  return null;
}

function browserGuess() {
  try {
    const lang = (navigator.language || "en").toLowerCase();
    if (lang.startsWith("it")) return "it";
    return "en";
  } catch (_e) { return "en"; }
}

const I18nContext = createContext({
  lang: "it",
  setLang: () => {},
  t: (k) => k,
  needsChoice: false,
  choose: () => {},
  dateLocale: "it-IT",
  formatDate: (iso) => iso,
});

function fmt(template, vars) {
  if (!vars) return template;
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ""));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => detectInitialLang() || browserGuess());
  const [needsChoice, setNeedsChoice] = useState(() => detectInitialLang() === null);

  const setLang = (l) => {
    if (!SUPPORTED_LANGS.includes(l)) return;
    setLangState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch (_e) { /* noop */ }
  };
  const choose = (l) => {
    setLang(l);
    setNeedsChoice(false);
  };

  const value = useMemo(() => {
    const dict = DICT[lang] || DICT.it;
    const dateLocale = lang === "en" ? "en-US" : "it-IT";
    const t = (key, vars) => {
      const raw = dict[key];
      if (raw === undefined) return key;
      if (Array.isArray(raw)) return raw;
      return fmt(raw, vars);
    };
    const formatDate = (iso, opts) => {
      if (!iso) return "";
      try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(dateLocale, opts || { year: "numeric", month: "2-digit", day: "2-digit" });
      } catch (_e) { return iso; }
    };
    const formatDateTime = (iso) =>
      formatDate(iso, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    return { lang, setLang, t, needsChoice, choose, dateLocale, formatDate, formatDateTime };
  }, [lang, needsChoice]);

  // Reflect lang in <html lang="..">
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (_e) { /* noop */ }
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);
